import axios from 'axios'

// All DIY live wallpaper records (returned unpaginated; the manager paginates
// client-side, newest first).
export const getAllLiveItems = async () => {
  const { data } = await axios.get('/api/live_items')
  return data
}

// Metadata-only edit. patch = { name, tags, price, sortPriority, freeDownload, isVisible, flowerCost, freeTrialDays, freeOnDaysOfWeek, maxTrialDays }.
export const updateLiveItem = async (itemId, patch) => {
  const { data } = await axios.patch(`/api/live_items/${encodeURIComponent(itemId)}`, patch)
  return data
}

// Bulk metadata edit: applies the same `patch` fields to every id in `itemIds`.
// -> { matched, modified, fields }
export const bulkUpdateLiveItems = async (itemIds, patch) => {
  const { data } = await axios.patch('/api/live_items/bulk', { itemIds, patch })
  return data
}

export const deleteLiveItem = async (itemId) => {
  const { data } = await axios.delete(`/api/live_items/${encodeURIComponent(itemId)}`)
  return data
}

// Bulk Google Play one-time purchase prices for every SKU, per currency.
// -> { [itemId]: { USD: number|null, TWD: number|null } }
export const getAllGooglePrices = async () => {
  const { data } = await axios.get('/api/google-play/onetime')
  return data?.prices || {}
}

// Google Play one-time purchase price for a SKU (== itemId).
// -> { exists, prices: { USD: number|null, TWD: number|null } }
export const getGooglePrice = async (itemId) => {
  const { data } = await axios.get(`/api/google-play/onetime/${encodeURIComponent(itemId)}`)
  return data
}

// Set the base price in `currency` ('USD' | 'TWD'); the chosen currency's home
// region gets exactly this amount and Google auto-converts every other region.
// -> { created, regions }
export const setGooglePrice = async (itemId, price, currency, name, description, purchaseOptionId) => {
  const { data } = await axios.put(`/api/google-play/onetime/${encodeURIComponent(itemId)}`, { price, currency, name, description, purchaseOptionId })
  return data
}

// Apply one base price to many SKUs. Max 25 itemIds per request — the caller is
// responsible for chunking a bigger selection. Resolves (not throws) when only
// SOME SKUs fail; inspect `results` for per-item outcomes.
// -> { results: [{ productId, ok, created, skipped, error }], created, updated, skipped, failed }
export const bulkSetGooglePrice = async (itemIds, { price, currency, name, description, createMissing }) => {
  const { data } = await axios.put('/api/google-play/onetime/bulk', { itemIds, price, currency, name, description, createMissing })
  return data
}

// ---- App Store (Apple) — the iOS twins of the Google Play calls above. -------
// Apple product id == itemId (unified with Google Play). Prices are USD-only (Apple auto-derives other regions)
// and snap to Apple's closest fixed price point.

// Bulk App Store prices for every DIY in-app purchase.
// -> { [itemId]: { USD: number|null, state } }
export const getAllApplePrices = async () => {
  const { data } = await axios.get('/api/app-store/onetime')
  return data?.prices || {}
}

// App Store price for one itemId. -> { exists, prices: { USD }, state }
export const getApplePrice = async (itemId) => {
  const { data } = await axios.get(`/api/app-store/onetime/${encodeURIComponent(itemId)}`)
  return data
}

// Set one itemId's App Store USD price (creates the in-app purchase when
// missing). -> { created, appliedPrice, requestedPrice, state }
export const setApplePrice = async (itemId, price, name, description) => {
  const { data } = await axios.put(`/api/app-store/onetime/${encodeURIComponent(itemId)}`, { price, name, description })
  return data
}

// Apply one USD price to many itemIds. Max 10 per request (backend BULK_MAX,
// lower than Google — Apple costs more round-trips per SKU); the caller chunks.
// Resolves (not throws) when only SOME SKUs fail; inspect `results`.
// -> { results: [{ itemId, productId, ok, created, skipped, appliedPrice, error }],
//      created, updated, skipped, failed }
export const bulkSetApplePrice = async (itemIds, { price, name, description, createMissing }) => {
  const { data } = await axios.put('/api/app-store/onetime/bulk', { itemIds, price, name, description, createMissing })
  return data
}
